import React, {useCallback, useEffect, useState} from "react";
import { useDispatch } from "react-redux";
import type IDrillholesCodeNewModalProps from "./IDrillholesCodeNewModalProps";
import useCalculationNewHook from "../../../../../../../hooks/useCalculationNewHook";
import { commitEditedDrillholes } from "../../../../../constructionCalculationsNewSlice";
import { validateDrillholesCodeWrapper } from "../../../../../constructionCalculationsNewSliceThunks";
import IsaacModal from "../../../../../../../components/isaacModal/IsaacModal";
import styles from "../../calculationConstructionNewField.module.scss";
import { sections } from "./Sections";
import type DrillholeDto from "../../../../../../../dto/calculations/DrillholeDto";
import type DrillholeEditDto from "../../../../../types/DrillholeEditDto";
import type { AppDispatch } from "../../../../../../../store";
import { DefaultButton, PrimaryButton } from "@fluentui/react";
import DrillholesCodeModalInputField from "./inputField/DrillholesCodeModalInputField";
import { sortDrillholeDtos } from "../utils/DrillholesCodeUtils";
import { orderInWhichDrillholesShouldAppear } from "./OrderOfDrillholes";
import type ISection from "./Section";

const initEditingDrillholes = (
  drillholes: DrillholeDto[],
  allSections: ISection[],
): DrillholeEditDto[] => {
  const allUiDrillholes = allSections.flatMap(s => s.drillholes);

  // Group UI drillholes by (symbol, hasIndex) key to track positional order
  const uiDrillholesBySymbolAndIndex = new Map<string, typeof allUiDrillholes>();
  for (const uiDrillhole of allUiDrillholes) {
    const index = uiDrillhole.hasIndex ? "1" : "0";
    const key = uiDrillhole.symbol + index;
    const group = uiDrillholesBySymbolAndIndex.get(key) ?? [];
    group.push(uiDrillhole);
    uiDrillholesBySymbolAndIndex.set(key, group);
  }

  // Map each drillhole to its corresponding UI drillhole by occurrence order;
  // excess drillholes beyond defined slots are silently dropped.
  const drillholeBySymbolAndIndex = new Map<string, number>();
  const editableDrillholes: DrillholeEditDto[] = [];
  for (const dh of drillholes) {
    const index = dh.number !== null ? "1" : "0";
    const key = dh.symbol + index;
    const occurrenceIndex = drillholeBySymbolAndIndex.get(key) ?? 0;
    drillholeBySymbolAndIndex.set(key, occurrenceIndex + 1);
    const uiDrillhole = uiDrillholesBySymbolAndIndex.get(key)?.[occurrenceIndex];
    if (uiDrillhole) {
      editableDrillholes.push({id: uiDrillhole.id, symbol: dh.symbol, number: dh.number});
    }
  }
  return editableDrillholes;
};

const DrillholesCodeNewModal: React.FunctionComponent<IDrillholesCodeNewModalProps> = ({
    calculationId,
    drillholesCode,
    selectedMast,
    isOpen,
    closeModal,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const {updateDrillholesCode, validateDrillholesCode} = useCalculationNewHook();

    const mastId: string = selectedMast?.mastId ?? "";
    const selectedCalculationId: string = calculationId ?? "";

    const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
    const [editingDrillholes, setEditingDrillholes] = useState<DrillholeEditDto[]>(() =>
      initEditingDrillholes(drillholesCode.drillholes, sections),
    );
    const [isValid, setIsValid] = useState<boolean>(drillholesCode.isValid);

    // Reset editing state when the modal transitions to open (setState during render
    // avoids cascading renders that would occur with useEffect + setState).
    if (prevIsOpen !== isOpen) {
      setPrevIsOpen(isOpen);
      if (isOpen) {
        setEditingDrillholes(initEditingDrillholes(drillholesCode.drillholes, sections));
        setIsValid(drillholesCode.isValid);
      }
    }

    useEffect(() => {
      let cancelled = false;
      const drillholes: DrillholeDto[] = editingDrillholes.map(({symbol, number}) => ({symbol, number}));
      void validateDrillholesCode(drillholes).then(result => {
        if (!cancelled) {
          setIsValid(result.errors === undefined);
        }
      });
      return () => { cancelled = true; };
    }, [editingDrillholes, validateDrillholesCode]);

    const handleAdd = useCallback((id: string, symbol: string, hasIndex: boolean): void => {
      setEditingDrillholes(prev =>
        sortDrillholeDtos(orderInWhichDrillholesShouldAppear, [
          ...prev,
          {id, symbol, number: hasIndex ? 0 : null},
        ]),
      );
    }, []);

    const handleRemove = useCallback((id: string): void => {
      setEditingDrillholes(prev => prev.filter(dh => dh.id !== id));
    }, []);

    const handleNumberChange = useCallback((id: string, newNumber: number): void => {
      setEditingDrillholes(prev =>
        prev.map(dh => (dh.id === id ? {...dh, number: newNumber} : dh)),
      );
    }, []);

    const editingValue = editingDrillholes.map(dh => dh.symbol + (dh.number ?? "")).join("");

    const handleSaveButtonClick = (): void => {
      const drillholes: DrillholeDto[] = editingDrillholes.map(({symbol, number}) => ({symbol, number}));
      dispatch(commitEditedDrillholes({mastId, drillholes}));
      void dispatch(validateDrillholesCodeWrapper({mastId, validateDrillholesCode}));
      void updateDrillholesCode(selectedCalculationId, mastId, drillholes);
      closeModal();
    };

    const handleCancelButtonClick = (): void => {
      closeModal();
    };

    return (
      <IsaacModal isOpen={isOpen} handleDismiss={closeModal}>
        <h1 className={styles.drillholesCodeModalTitle}>
          Drillholes Code Editor
        </h1>
        <div className={styles.drillholesCodeModal}>
          <h2
            className={[
              !isValid && styles.redUnderline,
              styles.drillholesCode,
            ].join(" ")}>
            {editingValue}
          </h2>
          <div className={styles.drillholesCodeModalInputs}>
            {(() => {
              const byId = new Map(editingDrillholes.map(dh => [dh.id, dh]));
              return sections.map(section => (
                <div
                  key={section.name}
                  className={styles.drillholesCodeModalSection}>
                  <div className={styles.drillholesCodeModalSectionName}>
                    {section.name}
                  </div>
                  <div className={styles.drillholesCodeModalSectionColumn}>
                    {section.drillholes.map(uiDrillhole => {
                      const drillholeEditDto = byId.get(uiDrillhole.id);
                      const isDrillholeMatched = drillholeEditDto !== undefined;

                      return (
                        <div
                          key={uiDrillhole.id}
                          className={styles.drillholesCodeModalInputStyle}>
                          <DrillholesCodeModalInputField
                            uiDrillhole={uiDrillhole}
                            isDrillholeMatched={isDrillholeMatched}
                            drillholeEditDto={drillholeEditDto}
                            onToggle={isChecked => {
                              if (isChecked) {
                                handleAdd(uiDrillhole.id, uiDrillhole.symbol, uiDrillhole.hasIndex);
                              } else if (drillholeEditDto) {
                                handleRemove(drillholeEditDto.id);
                              }
                            }}
                            onNumberChange={newNumber => {
                              if (drillholeEditDto) {
                                handleNumberChange(drillholeEditDto.id, newNumber);
                              }
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>
          <div className={styles.drillholesCodeModalButtonsContainer}>
            <PrimaryButton
              className={styles.drillholesCodeModalSaveButton}
              onClick={handleSaveButtonClick}
              text="Save"
            />
            <DefaultButton onClick={handleCancelButtonClick} text="Cancel" />
          </div>
        </div>
      </IsaacModal>
  );
};

export default DrillholesCodeNewModal;